import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { Download, Share, PlusSquare, Smartphone, Users, Briefcase, Truck, Printer, CheckCircle, MoreVertical } from 'lucide-react';
import { Button } from '@/components/ui/button';

const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
const isStandalone = () => window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;

const AUDIENCES = [
  { icon: Users, id: 'parents', title: 'Parents', points: ['Check your child in and out', 'Daily reports, photos & messages', 'Pay tuition securely', 'Van ETA texts and alerts'] },
  { icon: Briefcase, id: 'staff', title: 'Staff', points: ['Time clock with GPS check-in', 'Attendance & ratios', 'Write AI daily reports', 'Lesson plans and menus'] },
  { icon: Truck, id: 'drivers', title: 'Drivers', points: ['Quick 4-digit PIN login', "Today's manifest in route order", 'Tap kids on and off the van', 'Live route map'] },
];

const useInstallPrompt = () => {
  const [prompt, setPrompt] = useState(() => window.__ckInstallPrompt || null);
  const [installed, setInstalled] = useState(isStandalone());
  useEffect(() => {
    const onPrompt = (e) => { e.preventDefault(); window.__ckInstallPrompt = e; setPrompt(e); };
    const onInstalled = () => setInstalled(true);
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);
    return () => { window.removeEventListener('beforeinstallprompt', onPrompt); window.removeEventListener('appinstalled', onInstalled); };
  }, []);
  const install = async () => {
    if (!prompt) return;
    prompt.prompt();
    const { outcome } = await prompt.userChoice;
    if (outcome === 'accepted') setInstalled(true);
    window.__ckInstallPrompt = null;
    setPrompt(null);
  };
  return { canInstall: !!prompt, installed, install };
};

const Step = ({ n, icon: Icon, children }) => (
  <li className="flex items-start gap-4">
    <span className="w-8 h-8 shrink-0 rounded-full bg-primary text-white font-bold flex items-center justify-center">{n}</span>
    <span className="pt-1 flex items-center gap-2 flex-wrap">{children}{Icon && <Icon className="w-5 h-5 text-primary" />}</span>
  </li>
);

const InstallCard = () => {
  const { canInstall, installed, install } = useInstallPrompt();
  const ios = isIOS();
  if (installed) {
    return (
      <div className="card-clay p-8 flex items-center gap-4" data-testid="download-installed">
        <CheckCircle className="w-8 h-8 text-green-600" />
        <p className="font-semibold">The CareKids WI app is installed on this device.</p>
      </div>
    );
  }
  return (
    <div className="card-clay p-8" data-testid="download-install-card">
      {canInstall && !ios && (
        <Button className="btn-primary text-lg px-8 py-6 mb-8" onClick={install} data-testid="download-install-btn">
          <Download className="mr-2 w-5 h-5" /> Install the App
        </Button>
      )}
      <div className="grid md:grid-cols-2 gap-8">
        <div data-testid="download-ios-steps">
          <h3 className="font-heading font-bold text-lg mb-4">iPhone & iPad (Safari)</h3>
          <ol className="space-y-4">
            <Step n={1}>Open this page in <b>Safari</b></Step>
            <Step n={2} icon={Share}>Tap the <b>Share</b> button</Step>
            <Step n={3} icon={PlusSquare}>Choose <b>Add to Home Screen</b></Step>
            <Step n={4}>Tap <b>Add</b> and open CareKids from your home screen</Step>
          </ol>
        </div>
        <div data-testid="download-android-steps">
          <h3 className="font-heading font-bold text-lg mb-4">Android (Chrome)</h3>
          <ol className="space-y-4">
            <Step n={1}>Tap <b>Install the App</b> if you see it above, or</Step>
            <Step n={2} icon={MoreVertical}>Tap the <b>menu</b> in Chrome</Step>
            <Step n={3}>Choose <b>Install app</b> or <b>Add to Home screen</b></Step>
            <Step n={4}>Open CareKids from your home screen</Step>
          </ol>
        </div>
      </div>
    </div>
  );
};

const QrPoster = () => {
  const url = `${window.location.origin}/download`;
  return (
    <div className="card-clay p-8 flex flex-col sm:flex-row items-center gap-8 print:shadow-none" data-testid="download-qr-card">
      <div className="bg-white p-4 rounded-2xl ring-1 ring-black/5">
        <QRCodeSVG value={url} size={180} level="M" data-testid="download-qr-code" />
      </div>
      <div className="flex-1 text-center sm:text-left">
        <h3 className="font-heading font-bold text-2xl mb-2">Scan to get the CareKids app</h3>
        <p className="text-muted-foreground mb-1">Point your phone camera at the code.</p>
        <p className="text-sm text-muted-foreground mb-6 break-all">{url}</p>
        <Button variant="outline" className="print:hidden" onClick={() => window.print()} data-testid="download-print-qr-btn">
          <Printer className="mr-2 w-4 h-4" /> Print QR poster
        </Button>
      </div>
    </div>
  );
};

const Audiences = () => (
  <div className="grid md:grid-cols-3 gap-6 print:hidden">
    {AUDIENCES.map(({ icon: Icon, id, title, points }) => (
      <div key={id} className="card-clay p-6" data-testid={`download-audience-${id}`}>
        <div className="w-12 h-12 bg-primary/10 rounded-2xl flex items-center justify-center text-primary mb-4"><Icon className="w-6 h-6" /></div>
        <h3 className="font-heading font-bold text-xl mb-3">{title}</h3>
        <ul className="space-y-2">
          {points.map((p) => (
            <li key={p} className="flex items-start gap-2 text-sm text-muted-foreground"><CheckCircle className="w-4 h-4 mt-0.5 text-primary shrink-0" />{p}</li>
          ))}
        </ul>
      </div>
    ))}
  </div>
);

export default function DownloadPage() {
  return (
    <div className="min-h-screen bg-background" data-testid="download-page">
      <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-lg border-b border-border/50 print:hidden">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex justify-between items-center h-16">
          <Link to="/" className="flex items-center gap-2" data-testid="download-nav-home">
            <img src="/icons/icon-192x192.png?v=4" alt="CareKids WI" className="w-11 h-11 object-contain" />
            <span className="font-heading font-bold text-xl">CareKids WI</span>
          </Link>
          <Link to="/login"><Button variant="ghost" className="font-semibold" data-testid="download-login-btn">Sign In</Button></Link>
        </div>
      </nav>
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-14 space-y-10">
        <header className="max-w-2xl animate-slide-up print:hidden">
          <span className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 text-primary rounded-full font-semibold text-sm mb-5">
            <Smartphone className="w-4 h-4" /> No app store needed
          </span>
          <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl leading-tight mb-4">Get the CareKids WI app</h1>
          <p className="text-lg text-muted-foreground">One app for parents, staff and drivers. Install it in seconds, it opens full-screen like any other app and updates itself automatically.</p>
        </header>
        <InstallCard />
        <Audiences />
        <QrPoster />
      </main>
    </div>
  );
}
